import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProjectCard from "./ProjectCard";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import { motion } from "framer-motion";
import { usePageLoading } from "@/hooks/usePageLoading";
import ButtonElement from "@/components/elements/Button/Button";
import { fetchProjectCards } from "@/lib/queries";
import { fetchLocalProjects, type LocalImageVariants } from "@/lib/localProjects";
import type { SanityImageData } from "@/types/sanity";

interface Project {
  _id?: string;
  id?: string;
  title: string;
  category: string;
  slug?: string | null;
  /** Static image URL (local projects). */
  imageUrl?: string;
  /** Thumbnails for the local image, when they exist. */
  variants?: LocalImageVariants;
  /** Sanity image (CMS projects). */
  image?: SanityImageData | null;
  _updatedAt: string;
}

const MAIN_CATEGORIES = ["Ongoing Projects", "Completed Projects"];

const ProjectListPage = () => {
  const { isLoading } = usePageLoading();
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize selectedCategory based on URL pathname
  // so UI reflects URL even on page reload
  const initialCategory =
    location.pathname === "/projects/completed-subcategories"
      ? "Completed Projects"
      : "Ongoing Projects";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // CMS and local projects are independent sources: load them in parallel and show
  // whatever succeeds instead of losing everything when one request fails.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const loadProjects = async () => {
      const [sanityResult, localResult] = await Promise.allSettled([
        fetchProjectCards(null, { signal }),
        fetchLocalProjects(signal),
      ]);

      if (signal.aborted) return;

      if (sanityResult.status === "rejected") {
        console.error("Failed to load projects from Sanity:", sanityResult.reason);
      }
      if (localResult.status === "rejected") {
        console.error("Failed to load local projects:", localResult.reason);
      }

      const sanityProjects: Project[] =
        sanityResult.status === "fulfilled" ? sanityResult.value : [];

      // Local projects have no modification date; stamp them so they sort consistently.
      const localProjects: Project[] =
        localResult.status === "fulfilled"
          ? localResult.value.map((project) => ({
              ...project,
              _updatedAt: new Date().toISOString(),
            }))
          : [];

      setProjects([...sanityProjects, ...localProjects]);
      setLoading(false);
    };

    loadProjects();

    return () => controller.abort();
  }, []);

  // When category button clicked, update state AND navigate
  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    if (category === "Completed Projects") {
      navigate("/projects/completed-subcategories");
    } else {
      navigate("/projects");
    }
  };

  // Filter projects by selected category (only used for ongoing here)
 const filteredProjects = projects
  .filter(p => p.category === selectedCategory)
  .sort((a, b) => new Date(b._updatedAt).getTime() - new Date(a._updatedAt).getTime());
  
  if (loading) return <p className="mt-[5rem] min-h-screen pt-24 text-center text-gray-500">Loading projects...</p>;

  return (
    <div className="2xl:px-40 xl:px-35 lg:px-20 md:px-15 px-3 my-[5rem] ">
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={!isLoading ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
        transition={{ duration: 0.5 }}
      >
        <TitleWithContent title="Project Categories" level={1} />
      </motion.div>

      <div className="flex flex-col md:flex-row gap-10 px-5 md:px-0">
        {/* Sidebar Categories */}
        <div className="relative w-full max-w-[320px] mx-auto 2xl:ml-[7rem] xl:ml-[5rem] lg:ml-[4rem] flex flex-col">
          {MAIN_CATEGORIES.map((category) => {
            const isActive = selectedCategory === category;
            return (
              <ButtonElement
                key={category}
                btnText={category}
                onClick={() => handleCategoryClick(category)}
                className={`w-full rounded-sm !py-3 flex text-left mt-[1px] ${
                  isActive
                    ? "bg-custom-secolor font-semibold border-l-4 border-white bg-opacity-70"
                    : "bg-custom-secolor hover:bg-custom-secolor"
                }`}
              />
            );
          })}
        </div>

        {/* Project Cards for Ongoing Projects */}
        <div className="flex-1 ">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {selectedCategory === "Ongoing Projects" &&
              filteredProjects.map((project, index) => (
                <ProjectCard
                  key={project._id || project.id || project.slug}
                  id={project.id}
                  slug={project.slug ?? undefined}
                  imageUrl={project.imageUrl}
                  variants={project.variants}
                  priority={index < 6}
                  image={project.image}
                  title={project.title}
                  category={project.category}
                />
              ))}
            {/* If Completed Projects is selected, we don't show projects here,
                since that page has its own subcategory component */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectListPage;
